import { Alert, Box, Divider, Typography } from '@mui/material';
import { FormikErrors, useFormikContext } from 'formik';
import { compact, find, isString } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControls,
  FormikCheckbox,
  FormikNumericField,
  FormikTextField,
  PaymentTypeAmountLimits,
  TradeMethodTreeSelect,
} from 'components';
import { P2PProviderType } from 'enums';
import { useCurrencies, useMeta, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { TradeMethod } from 'types';

import { ConfigOverridesField } from '../ConfigOverridesField';
import { P2P_PROVIDER_TYPES_WITH_NO_PAYOUT_PROTECTION } from '../constants';
import { P2PProviderCredentials } from '../P2PProviderCredentials';

type Props = {
  providerType?: P2PProviderType | string;
  fiatCurrencyId?: string;
  assetCurrencyId?: string;
  onTradeMethodsChange: (event: any) => void;
  isNew?: boolean;
};

export const P2PProviderPayoutConfig: React.FC<Props> = ({
  providerType,
  fiatCurrencyId,
  isNew,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const { getFiatCurrencyCode } = useCurrencies();
  const { isP2PProviderCanIgnoreOrderPaymentTypeEnabled } = useMeta();

  const { tradeMethods, allTradeMethods, paymentTypes } = useUserContext();

  const availableTradeMethods: TradeMethod[] = useMemo(
    () => (isNew ? tradeMethods : allTradeMethods),
    [isNew, tradeMethods, allTradeMethods],
  );

  const fiatCurrenciesIds = useMemo(
    () => compact([fiatCurrencyId]),
    [fiatCurrencyId],
  );

  const { values, errors } = useFormikContext<any>();

  const ownPaymentType = useMemo(() => {
    if (
      !isP2PProviderCanIgnoreOrderPaymentTypeEnabled ||
      !values.payoutConfig?.useOwnPaymentType
    ) {
      return null;
    }
    const firstId = values.payoutConfig?.tradeMethods?.[0]?.id;
    const tradeMethod = find(allTradeMethods, (tm) => tm.id === firstId);
    return find(paymentTypes, (pt) => pt.id === tradeMethod?.paymentTypeId);
  }, [
    isP2PProviderCanIgnoreOrderPaymentTypeEnabled,
    values.payoutConfig?.useOwnPaymentType,
    values.payoutConfig?.tradeMethods,
    allTradeMethods,
    paymentTypes,
  ]);

  const multiplePaymentTypesError = useMemo(() => {
    const tradeMethodsErrors = (errors?.payoutConfig as FormikErrors<any>)
      ?.tradeMethods;
    return isP2PProviderCanIgnoreOrderPaymentTypeEnabled &&
      values.payoutConfig?.useOwnPaymentType &&
      isString(tradeMethodsErrors)
      ? tradeMethodsErrors
      : null;
  }, [
    values.payoutConfig?.useOwnPaymentType,
    isP2PProviderCanIgnoreOrderPaymentTypeEnabled,
    errors?.payoutConfig,
  ]);

  return (
    <Fragment>
      <Typography variant="h5">{tCommon('common.payout')}</Typography>
      <FormikCheckbox label={t('fields.enabled')} name="payoutConfig.enabled" />
      <FormikCheckbox
        label={t('fields.polling_enabled')}
        name="payoutConfig.pollingEnabled"
      />
      {isP2PProviderCanIgnoreOrderPaymentTypeEnabled && (
        <FormikCheckbox
          name="payoutConfig.useOwnPaymentType"
          label={t('fields.use_own_payment_type')}
          tooltip={t('fields.use_own_payment_type_description')}
          {...(ownPaymentType && {
            helperText: t('fields.use_own_payment_type_chosen_value', {
              paymentTypeName: tCommon(`payment_types.${ownPaymentType.code}`, {
                defaultValue: ownPaymentType.name,
              }),
            }),
          })}
        />
      )}

      <FormikTextField
        label={t('fields.url')}
        name="payoutConfig.url"
        required={providerType !== P2PProviderType.ManualResolve}
      />

      <P2PProviderCredentials
        path="payoutConfig.credentials"
        providerType={providerType}
      />

      <FormikNumericField
        label={t('fields.response_timeout')}
        name="payoutConfig.responseTimeout"
        suffix={tCommon('suffixes.ms')}
        allowNegative={false}
        defaultValue={0}
        fullWidth
        helperText={t('fields.response_timeout_description')}
      />

      <FormikNumericField
        label={t('fields.order_timeout')}
        name="payoutConfig.orderTimeout"
        suffix={tCommon('suffixes.minutes')}
        allowNegative={false}
        required={!!values.payoutConfig?.enabled}
        defaultValue={0}
        fullWidth
        helperText={t('fields.order_timeout_description')}
      />

      <FormikNumericField
        label={t('fields.expected_fee')}
        name="payoutConfig.expectedFee"
        allowNegative={false}
        percentageSuffix
        fullWidth
        required
      />

      <Fragment>
        <Typography>
          {`${t('fields.trade_methods')} (${tCommon('common.payout')})`}
        </Typography>

        {!!multiplePaymentTypesError && (
          <Alert severity="error">{multiplePaymentTypesError}</Alert>
        )}

        <TradeMethodTreeSelect
          fieldName={'payoutConfig.tradeMethods'}
          tradeMethods={availableTradeMethods}
          fiatCurrenciesIds={fiatCurrenciesIds}
        />
      </Fragment>

      <Box sx={{ mt: 2, mb: 3 }}>
        <PaymentTypeAmountLimits
          configPath="payoutConfig"
          fiatCurrencyCode={getFiatCurrencyCode(fiatCurrencyId)}
        />
      </Box>

      <ConfigOverridesField
        fieldPath="payoutConfig"
        providerType={providerType}
      />

      {!P2P_PROVIDER_TYPES_WITH_NO_PAYOUT_PROTECTION.includes(
        providerType as P2PProviderType,
      ) && (
        <Fragment>
          <Divider sx={{ mt: 2, mb: 2 }}>
            {t('fields.response_classification_enabled')}
          </Divider>
          <FormikTextField
            label={t('fields.status_field_path')}
            name="payoutConfig.payoutResponseTemplates.statusFieldPath"
            helperText={t('fields.status_field_path_description')}
            fullWidth
          />
          <FormikTextField
            label={t('fields.accepted_statuses')}
            name="payoutConfig.payoutResponseTemplates.acceptedStatuses"
            helperText={t('fields.accepted_statuses_description')}
            multiline
            fullWidth
          />
          <FormikTextField
            label={t('fields.rejected_statuses')}
            name="payoutConfig.payoutResponseTemplates.rejectedStatuses"
            helperText={t('fields.rejected_statuses_description')}
            multiline
            fullWidth
          />
          <FormikTextField
            label={t('fields.required_response_fields')}
            name="payoutConfig.payoutResponseTemplates.requiredResponseFields"
            helperText={t('fields.required_response_fields_description')}
            multiline
            fullWidth
          />
          <FormikTextField
            label={t('fields.rejected_http_status_codes')}
            name="payoutConfig.payoutResponseTemplates.rejectedHttpStatusCodes"
            helperText={t('fields.rejected_http_status_codes_description')}
            multiline
            fullWidth
          />
          <FormikTextField
            label={t('fields.error_indicators')}
            name="payoutConfig.payoutResponseTemplates.errorIndicators"
            helperText={t('fields.error_indicators_description')}
            placeholder={
              '[{ "fieldPath": "error_code", "presentAndTruthy": true }]'
            }
            multiline
            minRows={3}
            fullWidth
          />
          <FormikTextField
            label={t('fields.acceptance_indicators')}
            name="payoutConfig.payoutResponseTemplates.acceptanceIndicators"
            helperText={t('fields.acceptance_indicators_description')}
            placeholder={
              '[{ "fieldPath": "transaction_id", "presentAndTruthy": true }]'
            }
            multiline
            minRows={3}
            fullWidth
          />
        </Fragment>
      )}

      <Divider sx={{ mt: 2, mb: 2 }}>{t('fields.order_amount_limits')}</Divider>
      <FormControls row>
        <FormikNumericField
          name="payoutConfig.orderMinAmount"
          label={tCommon('common.min')}
          fullWidth
          suffix={getFiatCurrencyCode(fiatCurrencyId)}
          allowNegative={false}
        />
        <FormikNumericField
          name="payoutConfig.orderMaxAmount"
          label={tCommon('common.max')}
          fullWidth
          suffix={getFiatCurrencyCode(fiatCurrencyId)}
          allowNegative={false}
        />
      </FormControls>
    </Fragment>
  );
};
